// Colunas da tabela de templates (§4.4) — função para permitir
// i18n e injetar as ações (abrir editor / duplicar). Colunas: nome ·
// WL · canal · versão ativa · nº de versões · "plugado em" ·
// atualizado em · ações. Canal, nº de versões e "plugado em" vêm do
// enriquecimento por linha (template-enrichment-cells).
import type { ColumnDef } from "@tanstack/react-table"
import type { TFunction } from "i18next"
import {
  CopyPlusIcon,
  MoreHorizontalIcon,
  PencilIcon,
} from "lucide-react"

import { ClipboardButton } from "@/components/commons/clipboard-button"
import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import type { NotificationCenterWhitelabelTemplate } from "@/types/notification-center"
import { DateHelper } from "@/utils"

import {
  TemplateChannelCell,
  TemplateUsedByCell,
  TemplateVersionsCountCell,
} from "./template-enrichment-cells"

const templatesPrefix =
  "pages.notificationCenter.templatesPage"

const formatDateCell = (value?: string) => {
  if (!value) return "—"
  return DateHelper.format(value, "dd/MM/yyyy HH:mm:ss")
}

// Callbacks das ações de linha (navegação/duplicação ficam no
// container — colunas permanecem função pura).
export type TemplateColumnActions = {
  onOpenEditor: (
    template: NotificationCenterWhitelabelTemplate
  ) => void
  onDuplicate: (
    template: NotificationCenterWhitelabelTemplate
  ) => void
}

export const getNotificationCenterTemplateColumns = (
  t: TFunction,
  actions: TemplateColumnActions
): ColumnDef<NotificationCenterWhitelabelTemplate>[] => [
  {
    header: t(`${templatesPrefix}.columns.name`),
    accessorKey: "name",
    cell: ({ row }) => (
      <div className="flex flex-col gap-0.5">
        <button
          type="button"
          className="text-left text-sm font-medium hover:underline"
          onClick={() => actions.onOpenEditor(row.original)}
        >
          {row.original.name}
        </button>
        <span className="flex items-center gap-1 text-xs text-muted-foreground">
          {row.original.id}
          <ClipboardButton
            aria-label={t(
              `${templatesPrefix}.copyTemplateId`
            )}
            title={t(`${templatesPrefix}.copyTemplateId`)}
            text={row.original.id}
            message={t(
              `${templatesPrefix}.copiedTemplateId`
            )}
            className="shrink-0"
          />
        </span>
      </div>
    ),
  },
  {
    id: "channel",
    header: t(`${templatesPrefix}.columns.channel`),
    cell: ({ row }) => (
      <TemplateChannelCell channel={row.original.channel} />
    ),
  },
  {
    header: t(`${templatesPrefix}.columns.activeVersion`),
    accessorKey: "version",
    // version é INTEIRO (ex.: 1, 2, 3) — sem toFixed.
    cell: ({ row }) => (
      <div className="tabular-nums">
        {row.original.version ?? "—"}
      </div>
    ),
  },
  {
    id: "versionsCount",
    header: t(`${templatesPrefix}.columns.versionsCount`),
    cell: ({ row }) => (
      <TemplateVersionsCountCell
        count={row.original.versionsCount}
      />
    ),
  },
  {
    id: "usedBy",
    header: t(`${templatesPrefix}.columns.usedBy`),
    cell: ({ row }) => (
      <TemplateUsedByCell usedBy={row.original.usedBy} />
    ),
  },
  {
    header: t(`${templatesPrefix}.columns.updatedAt`),
    accessorKey: "updatedAt",
    cell: ({ row }) => (
      <div>{formatDateCell(row.original.updatedAt)}</div>
    ),
  },
  {
    id: "actions",
    header: t(`${templatesPrefix}.columns.actions`),
    cell: ({ row }) => (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            aria-label={t(
              `${templatesPrefix}.rowActionsLabel`
            )}
          >
            <MoreHorizontalIcon
              className="size-4"
              aria-hidden="true"
            />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end">
          <DropdownMenuItem
            onClick={() =>
              actions.onOpenEditor(row.original)
            }
          >
            <PencilIcon
              className="mr-2 size-4"
              aria-hidden="true"
            />
            {t(`${templatesPrefix}.openEditor`)}
          </DropdownMenuItem>
          <DropdownMenuItem
            onClick={() =>
              actions.onDuplicate(row.original)
            }
          >
            <CopyPlusIcon
              className="mr-2 size-4"
              aria-hidden="true"
            />
            {t(`${templatesPrefix}.duplicate`)}
          </DropdownMenuItem>
        </DropdownMenuContent>
      </DropdownMenu>
    ),
  },
]
